import React, { useEffect, useRef, useState } from 'react';
import { Modal, TextInput, ActionIcon, Tooltip, Indicator, Checkbox, Group, Button as MantineButton, Select } from '@mantine/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen as faPenDef,
  faTags as faTagsDef,
  faBan as faBanDef,
  faCircleCheck as faCircleCheckDef,
  faXmark as faXmarkRemoveDef,
} from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { CategoryView } from '../../../../../../services/categories/categories.service';
import type { ProductView } from '../../../../../../services/products/products.service';
import { Button } from '../../../../../../components/Button';
import { DragHandle } from '../../../../../../components/DragHandle/DragHandle';
import navyModal from '../../../../../../components/Modal/navy-modal.module.scss';
import { useCategoryManagerController } from './useCategoryManagerController';
import { CategoryMembershipDrawer } from '../category-membership-drawer/CategoryMembershipDrawer';
import { depthOf } from '../shared/category-manager.utils';
import shared from '../../shared/settings-shared.module.scss';
import productClasses from '../../shared/product-settings.module.scss';

const faPen = faPenDef as IconProp;
const faTags = faTagsDef as IconProp;
const faBan = faBanDef as IconProp;
const faCircleCheck = faCircleCheckDef as IconProp;
const faXmarkRemove = faXmarkRemoveDef as IconProp;

type SortableRowRenderProps = {
  setNodeRef: (element: HTMLElement | null) => void;
  setActivatorNodeRef: (element: HTMLElement | null) => void;
  style: React.CSSProperties;
  attributes: DraggableAttributes;
  listeners: DraggableSyntheticListeners;
};

// Architectural summary: dnd-kit sortable wrapper exposing drag props to a row via render-prop.
const SortableRow: React.FC<{ id: number; children: (drag: SortableRowRenderProps) => React.ReactNode }> = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };
  return <>{children({ setNodeRef, setActivatorNodeRef, style, attributes, listeners })}</>;
};

export type CategoryManagerModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
  categories: CategoryView[];
  products: ProductView[];
};

const CategoryManagerModalComponent: React.FC<CategoryManagerModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  categories,
  products,
}) => {
  const ctrl = useCategoryManagerController({ tenantId, employeeSession, categories, products });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    void ctrl.reorderCategory(Number(active.id), Number(over.id));
  };
  const categoryById = React.useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const isFormModalOpen = isCreateModalOpen || ctrl.editingId !== null;
  const isEditMode = ctrl.editingId !== null;

  const handleClose = () => {
    if (!ctrl.isAnyWriteInFlight) {
      onClose();
    }
  };

  const closeFormModal = () => {
    if (ctrl.isAnyWriteInFlight) return;
    setCreateModalOpen(false);
    ctrl.cancelEdit();
  };

  // isCreateModalOpen is local state — close it here on the same success signal
  // cancelEdit() already uses (rows collapsed back to one empty row).
  const wasSavingFormRef = useRef(false);
  useEffect(() => {
    if (wasSavingFormRef.current && !ctrl.isSavingForm && ctrl.rows.length === 1 && ctrl.rows[0].name === '' && isCreateModalOpen) {
      setCreateModalOpen(false);
    }
    wasSavingFormRef.current = ctrl.isSavingForm;
  }, [ctrl.isSavingForm, ctrl.rows, isCreateModalOpen]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        onContextMenu={(event) => event.preventDefault()}
        title="ניהול קטגוריות"
        size="xl"
        centered
        radius="md"
        classNames={{ root: navyModal.root, body: shared.narrowBody }}
        closeButtonProps={{ disabled: ctrl.isAnyWriteInFlight, 'data-testid': 'category-manager-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
        closeOnClickOutside={!ctrl.isAnyWriteInFlight}
        closeOnEscape={!ctrl.isAnyWriteInFlight}
      >
        <section className={shared.section}>
          <div className={shared.toolbar}>
            <Button type="button" variant="save" size="sm" onClick={() => setCreateModalOpen(true)} dataTestId="category-manager-create-open">
              צור קטגוריה
            </Button>
          </div>

          <div className={shared.tableWrap}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <table className={shared.table}>
              <thead>
                <tr>
                  <th className={productClasses.dragCell} aria-hidden />
                  <th>שם</th>
                  <th>קטגוריית אם</th>
                  <th>אוכל</th>
                  <th>משקאות</th>
                  <th>סטטוס</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <SortableContext items={ctrl.orderedCategories.map((category) => category.id)} strategy={verticalListSortingStrategy}>
              <tbody>
                {ctrl.orderedCategories.map((category) => (
                  <SortableRow key={category.id} id={category.id}>
                    {(drag) => (
                  <tr ref={drag.setNodeRef} style={drag.style} data-testid={`manage-category-${category.id}`}>
                    <td className={productClasses.dragCell}>
                      <DragHandle
                        dataTestId={`category-manager-drag-${category.id}`}
                        disabled={ctrl.isAnyWriteInFlight}
                        attributes={drag.attributes}
                        listeners={drag.listeners}
                        setActivatorNodeRef={drag.setActivatorNodeRef}
                      />
                    </td>
                    <td data-testid={`manage-category-name-${category.id}`}>
                      {'↳ '.repeat(depthOf(category.id, categoryById) - 1)}
                      {category.name}
                    </td>
                    <td className={shared.parentCell}>
                      <Select
                        data-testid={`category-manager-parent-${category.id}`}
                        aria-label={`קטגוריית אם עבור ${category.name}`}
                        placeholder="ללא (קטגוריה ראשית)"
                        clearable
                        radius="md"
                        size="sm"
                        data={ctrl.parentOptionsByCategory.get(category.id) ?? []}
                        value={category.parentCategoryPk === null ? null : String(category.parentCategoryPk)}
                        onChange={(value) => void ctrl.setCategoryParent(category, value === null ? null : Number(value))}
                        disabled={ctrl.isAnyWriteInFlight}
                      />
                    </td>
                    <td className={shared.checkboxCell}>
                      <Checkbox
                        data-testid={`category-manager-food-${category.id}`}
                        aria-label={`סמן אוכל עבור ${category.name}`}
                        checked={category.reportGroup === 'food'}
                        disabled={ctrl.isAnyWriteInFlight}
                        onChange={() => void ctrl.setCategoryReportGroup(category, 'food')}
                        size="md"
                      />
                    </td>
                    <td className={shared.checkboxCell}>
                      <Checkbox
                        data-testid={`category-manager-beverages-${category.id}`}
                        aria-label={`סמן משקאות עבור ${category.name}`}
                        checked={category.reportGroup === 'beverages'}
                        disabled={ctrl.isAnyWriteInFlight}
                        onChange={() => void ctrl.setCategoryReportGroup(category, 'beverages')}
                        size="md"
                      />
                    </td>
                    <td>
                      <span className={category.isActive ? shared.statusActive : shared.statusInactive}>
                        {category.isActive ? 'פעיל' : 'לא פעיל'}
                      </span>
                    </td>
                    <td>
                      <div className={shared.actionsCell}>
                        <Tooltip label="שנה שם" withArrow>
                          <ActionIcon
                            aria-label="שנה שם"
                            data-testid={`category-manager-edit-${category.id}`}
                            variant="subtle"
                            color="gray"
                            radius="md"
                            size="lg"
                            disabled={ctrl.isAnyWriteInFlight}
                            onClick={() => ctrl.startEdit(category)}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </ActionIcon>
                        </Tooltip>

                        <Tooltip label="שייך מוצרים" withArrow>
                          <Indicator
                            label={ctrl.productCountByCategory.get(category.id) ?? 0}
                            disabled={!(ctrl.productCountByCategory.get(category.id) ?? 0)}
                            color="blue"
                            size={18}
                            offset={4}
                          >
                            <ActionIcon
                              aria-label="שייך מוצרים"
                              data-testid={`category-manager-membership-${category.id}`}
                              variant="subtle"
                              color="blue"
                              radius="md"
                              size="lg"
                              disabled={ctrl.isAnyWriteInFlight}
                              onClick={() => ctrl.setMembershipCategoryId(category.id)}
                            >
                              <FontAwesomeIcon icon={faTags} />
                            </ActionIcon>
                          </Indicator>
                        </Tooltip>

                        {category.isActive ? (
                          <Tooltip label="השבת" withArrow>
                            <ActionIcon
                              aria-label="השבת"
                              data-testid={`category-manager-toggle-${category.id}`}
                              variant="subtle"
                              color="red"
                              radius="md"
                              size="lg"
                              loading={ctrl.togglingId === category.id}
                              disabled={ctrl.isAnyWriteInFlight}
                              onClick={() => void ctrl.toggleActive(category)}
                            >
                              <FontAwesomeIcon icon={faBan} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <Tooltip label="הפעל" withArrow>
                            <ActionIcon
                              aria-label="הפעל"
                              data-testid={`category-manager-toggle-${category.id}`}
                              variant="subtle"
                              color="green"
                              radius="md"
                              size="lg"
                              loading={ctrl.togglingId === category.id}
                              disabled={ctrl.isAnyWriteInFlight}
                              onClick={() => void ctrl.toggleActive(category)}
                            >
                              <FontAwesomeIcon icon={faCircleCheck} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                    )}
                  </SortableRow>
                ))}
                {ctrl.orderedCategories.length === 0 && (
                  <tr>
                    <td colSpan={7} className={shared.empty}>
                      לא הוגדרו קטגוריות עדיין.
                    </td>
                  </tr>
                )}
              </tbody>
              </SortableContext>
            </table>
            </DndContext>
          </div>
        </section>
      </Modal>

      <Modal
        opened={isFormModalOpen}
        onClose={closeFormModal}
        onContextMenu={(event) => event.preventDefault()}
        title={isEditMode ? 'עריכת קטגוריה' : 'קטגוריה חדשה'}
        size="md"
        centered
        radius="md"
        classNames={{ root: navyModal.root }}
        closeButtonProps={{ disabled: ctrl.isAnyWriteInFlight, 'aria-label': 'סגור', 'data-testid': 'category-manager-form-close' } as React.ComponentProps<typeof Modal>['closeButtonProps']}
        closeOnClickOutside={!ctrl.isAnyWriteInFlight}
        closeOnEscape={!ctrl.isAnyWriteInFlight}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void ctrl.submitForm();
          }}
          className={shared.section}
        >
          {ctrl.rows.map((row, index) => (
            <div key={row.rowKey} className={productClasses.productFormRow}>
              {index > 0 && (
                <div className={productClasses.productFormRowHeader}>
                  <ActionIcon
                    aria-label="הסר קטגוריה"
                    data-testid={`category-manager-form-remove-${index}`}
                    variant="subtle"
                    color="red"
                    radius="md"
                    size="sm"
                    disabled={ctrl.isAnyWriteInFlight}
                    onClick={() => ctrl.removeRow(index)}
                  >
                    <FontAwesomeIcon icon={faXmarkRemove} />
                  </ActionIcon>
                </div>
              )}
              <div className={shared.formRow}>
                <TextInput
                  data-testid={`category-manager-form-name-${index}`}
                  label="שם הקטגוריה"
                  placeholder="שם הקטגוריה"
                  value={row.name}
                  onChange={(event) => ctrl.updateRowField(index, 'name', event.currentTarget.value)}
                  minLength={1}
                  maxLength={120}
                  required
                  radius="md"
                />
                <Group gap="md">
                  <Checkbox
                    data-testid={`category-manager-form-food-${index}`}
                    label="אוכל"
                    checked={row.reportGroup === 'food'}
                    onChange={() => ctrl.updateRowField(index, 'reportGroup', row.reportGroup === 'food' ? null : 'food')}
                  />
                  <Checkbox
                    data-testid={`category-manager-form-beverages-${index}`}
                    label="משקאות"
                    checked={row.reportGroup === 'beverages'}
                    onChange={() => ctrl.updateRowField(index, 'reportGroup', row.reportGroup === 'beverages' ? null : 'beverages')}
                  />
                </Group>
                <Select
                  data-testid="category-manager-form-parent"
                  label="קטגוריית אם"
                  placeholder="ללא (קטגוריה ראשית)"
                  clearable
                  radius="md"
                  data={ctrl.parentCategoryOptions}
                  value={row.parentCategoryPk === null ? null : String(row.parentCategoryPk)}
                  onChange={(value) => ctrl.updateRowField(index, 'parentCategoryPk', value === null ? null : Number(value))}
                  disabled={ctrl.isAnyWriteInFlight}
                />
              </div>
            </div>
          ))}
          <div className={isEditMode ? shared.formActions : productClasses.productFormFooter}>
            {!isEditMode && (
              <MantineButton type="button" variant="default" radius="md" className={productClasses.addRowButton} data-testid="category-manager-form-add-row" onClick={ctrl.addRow} disabled={ctrl.isAnyWriteInFlight}>
                הוסף קטגוריה נוספת
              </MantineButton>
            )}
            <Button
              type="submit"
              variant="save"
              disabled={!ctrl.rows[0].name.trim() || ctrl.isAnyWriteInFlight}
              loading={ctrl.isSavingForm}
              loadingLabel="שומר..."
              dataTestId="category-manager-form-submit"
            >
              שמור
            </Button>
          </div>
        </form>
      </Modal>

      <CategoryMembershipDrawer
        category={ctrl.membershipCategory}
        activeProducts={ctrl.activeProducts}
        categoryById={ctrl.categoryById}
        checkedProductIds={ctrl.checkedProductIds}
        isSaving={ctrl.isAnyWriteInFlight}
        onToggleProduct={ctrl.toggleProduct}
        onSave={() => void ctrl.saveMembership()}
        onClose={() => ctrl.setMembershipCategoryId(null)}
      />
    </>
  );
};

export const CategoryManagerModal = React.memo(CategoryManagerModalComponent);
